/**
 * Line designations as the MCF receives them (31h NOM). The DGI MCF refuses
 * a second article whose NOM, byte for byte, is that of an article already
 * accepted on the invoice; case and a trailing space make another NOM, the
 * code, price and group do not.
 *
 * `uniqueMcfItemNames` gives each line of an invoice a designation the MCF
 * takes as its own; it runs once, when the invoice is issued, and the
 * designations it returns are the ones stored and printed.
 *
 * @module @efacture/fiscal-core/services/mcf-item-names
 */
/** Most characters of a NOM (annex §4.4). */
export const MCF_ITEM_NAME_MAX_CHARS = 64;
/**
 * The NOM the session plan sends for a designation: without surrounding
 * spaces, cut to `MCF_ITEM_NAME_MAX_CHARS` characters, without trailing
 * spaces.
 */
export function mcfItemName(designation) {
    return [...(designation ?? "").trim()].slice(0, MCF_ITEM_NAME_MAX_CHARS).join("").trimEnd();
}
const encoder = new TextEncoder();
const decoder = new TextDecoder();
/** A NOM as the bytes the frame carries (UTF-8), read back as a string. */
function wireKey(name) {
    return decoder.decode(encoder.encode(name));
}
/**
 * The lines whose NOM an earlier line already has, as pairs of 0-based
 * indexes (the earlier line, the line); empty when every NOM is unique.
 */
export function duplicateMcfItemNames(names) {
    const first = new Map();
    const duplicates = [];
    names.forEach((name, index) => {
        const key = wireKey(name);
        const seen = first.get(key);
        if (seen === undefined)
            first.set(key, index);
        else
            duplicates.push([seen, index]);
    });
    return duplicates;
}
/**
 * Designations that give every line its own NOM, in line order: a line
 * whose NOM an earlier line already has takes " (n)" after its
 * designation, n being its rank among the lines of that NOM (2, 3, …), or
 * the next free rank when a line already bears that designation. The
 * designation is cut so the suffix stays within the NOM length. Any other
 * designation is returned unchanged.
 */
export function uniqueMcfItemNames(designations) {
    const sent = designations.map(mcfItemName);
    const taken = new Set(sent.map(wireKey));
    const seen = new Map();
    return designations.map((designation, index) => {
        const key = wireKey(sent[index]);
        const rank = (seen.get(key) ?? 0) + 1;
        seen.set(key, rank);
        if (rank === 1)
            return designation;
        for (let n = rank;; n++) {
            const suffix = ` (${n})`;
            const base = [...sent[index]].slice(0, MCF_ITEM_NAME_MAX_CHARS - suffix.length).join("").trimEnd();
            const candidate = `${base}${suffix}`;
            if (!taken.has(wireKey(candidate))) {
                taken.add(wireKey(candidate));
                return candidate;
            }
        }
    });
}
//# sourceMappingURL=mcf-item-names.js.map